/* ═══════════════════════════════════════════════════════════════════
   APP.JS – GSAP, Lenis, SplitType, Lucide, Scroll Reveals
   ═══════════════════════════════════════════════════════════════════ */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { createIcons, BarChart3, ChevronDown, Clock, Cloud, Code2, GanttChart, GitMerge, Lightbulb, Linkedin, Lock, Mail, MapPin, Phone, RefreshCw, Send, Share2, ShieldCheck, Sparkles, Target, Users, VolumeX, Zap } from 'lucide';
import Lenis from 'lenis';
import SplitType from 'split-type';
import emailjs from '@emailjs/browser';

gsap.registerPlugin(ScrollTrigger);

(function () {
  'use strict';

  /* ── Lenis Smooth Scroll ── */
  const lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
  });

  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  /* ── Lucide Icons ── */
  createIcons({ icons: { BarChart3, ChevronDown, Clock, Cloud, Code2, GanttChart, GitMerge, Lightbulb, Linkedin, Lock, Mail, MapPin, Phone, RefreshCw, Send, Share2, ShieldCheck, Sparkles, Target, Users, VolumeX, Zap } });

  /* ── SplitType: Headline character animations ── */
  const splitHeadlines = document.querySelectorAll('.split-text');
  splitHeadlines.forEach((el) => {
    const split = new SplitType(el, { types: 'chars, words' });
    gsap.set(split.chars, { opacity: 0, y: 30 });

    ScrollTrigger.create({
      trigger: el,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(split.chars, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.03,
        });
      },
    });
  });

  /* ── Hero Staggered Load Animation ── */
  const heroTl = gsap.timeline({ delay: 0.3 });
  const heroStatsWrapper = document.querySelector('.hero__stats-surface');
  const heroTitle = document.querySelector('.hero__title');
  const heroSubtitle = document.querySelector('.hero__subtitle');
  const heroTagline = document.querySelector('.hero__tagline');
  const heroActions = document.querySelector('.hero__actions');
  const heroImage = document.querySelector('.hero__image');

  const isDesktop = !window.matchMedia('(max-width: 768px)').matches;

  // Set initial states
  if (heroTagline) gsap.set(heroTagline, { opacity: 0, y: -15 });
  if (heroTitle) gsap.set(heroTitle, { clipPath: 'inset(0 100% 0 0)', opacity: 1, y: 0 });
  if (heroSubtitle) gsap.set(heroSubtitle, { opacity: 0, x: -30 });
  if (heroActions) gsap.set(heroActions, { opacity: 0, y: 15 });
  if (heroStatsWrapper) gsap.set(heroStatsWrapper, { opacity: 0, y: 20 });
  if (heroImage) gsap.set(heroImage, { opacity: 0, x: isDesktop ? 40 : 0 });

  // Build timeline
  if (heroTagline) heroTl.to(heroTagline, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' });
  if (heroTitle) heroTl.to(heroTitle, { clipPath: 'inset(0 0% 0 0)', duration: 1.2, ease: 'power4.inOut' }, '-=0.3');
  if (heroImage) heroTl.to(heroImage, { opacity: 1, x: 0, duration: 1, ease: 'power3.out' }, '-=0.8');
  if (heroSubtitle) heroTl.to(heroSubtitle, { opacity: 1, x: 0, duration: 0.7, ease: 'power3.out' }, '-=0.5');
  if (heroActions) heroTl.to(heroActions, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }, '-=0.3');
  if (heroStatsWrapper) heroTl.to(heroStatsWrapper, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }, '-=0.2');

  /* ── Hero Magnetic Cursor 3D Effect (desktop only) ── */
  const heroSection = document.querySelector('.hero');
  const heroContent = document.querySelector('.hero__content');
  if (isDesktop && heroSection && heroContent && heroImage) {
    const xC = gsap.quickTo(heroContent, 'x', { duration: 0.6, ease: 'power3.out' });
    const yC = gsap.quickTo(heroContent, 'y', { duration: 0.6, ease: 'power3.out' });
    const xI = gsap.quickTo(heroImage, 'x', { duration: 0.8, ease: 'power3.out' });
    const yI = gsap.quickTo(heroImage, 'y', { duration: 0.8, ease: 'power3.out' });

    heroSection.addEventListener('mousemove', (e) => {
      const r = heroSection.getBoundingClientRect();
      const nx = (e.clientX - r.left - r.width / 2) / r.width;
      const ny = (e.clientY - r.top - r.height / 2) / r.height;
      xC(nx * 12); yC(ny * 8);
      xI(nx * -18); yI(ny * -12);
    });

    heroSection.addEventListener('mouseleave', () => {
      xC(0); yC(0); xI(0); yI(0);
    });
  }

  /* ── Hero Parallax: image drifts on scroll (desktop only) ── */
  if (isDesktop && heroImage) {
    gsap.to(heroImage, {
      yPercent: 15,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero',
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  }

  /* ── About Cells: clip-path reveal ── */
  const aboutCells = document.querySelectorAll('.about__cell');
  aboutCells.forEach((cell, i) => {
    gsap.set(cell, { clipPath: 'inset(12% 12% 12% 12%)', opacity: 0, scale: 0.96 });
    ScrollTrigger.create({
      trigger: cell,
      start: 'top 88%',
      once: true,
      onEnter: () => {
        gsap.to(cell, {
          clipPath: 'inset(0% 0% 0% 0%)',
          opacity: 1,
          scale: 1,
          duration: 0.9,
          ease: 'power3.out',
          delay: i * 0.06,
        });
      },
    });
  });

  /* ── Competencies: Clip-Path Cascade Reveal ── */
  const compCards = document.querySelectorAll('.competency-card');
  if (compCards.length) {
    const clipDirs = [
      'inset(0 100% 0 0)',   // wipe from left
      'inset(100% 0 0 0)',   // wipe from top
      'inset(0 0 0 100%)',   // wipe from right
      'inset(0 0 100% 0)',   // wipe from bottom
      'inset(0 100% 0 0)',   // wipe from left
      'inset(100% 0 0 0)',   // wipe from top
    ];

    compCards.forEach((card, i) => {
      gsap.set(card, { clipPath: clipDirs[i], opacity: 0 });
      ScrollTrigger.create({
        trigger: card,
        start: 'top 88%',
        once: true,
        onEnter: () => {
          gsap.to(card, {
            clipPath: 'inset(0 0 0 0)',
            opacity: 1,
            duration: 0.9,
            ease: 'power3.out',
            delay: i * 0.1,
          });
        },
      });
    });
  }

  /* ── GSAP: Generic reveal for .gsap-reveal elements ── */
  const gsapReveals = document.querySelectorAll('.gsap-reveal');
  if (gsapReveals.length) {
    gsap.set(gsapReveals, { opacity: 0, y: 40 });
    ScrollTrigger.batch(gsapReveals, {
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
          stagger: 0.1,
        });
      },
      start: 'top 88%',
      once: true,
    });
  }

  /* ── Career Rows: stagger slide-up with fade ── */
  const careerRows = document.querySelectorAll('.career__row:not(.career__row--header)');
  if (careerRows.length) {
    gsap.set(careerRows, { opacity: 0, y: 40, x: -20 });
    ScrollTrigger.batch(careerRows, {
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1, y: 0, x: 0,
          duration: 0.7,
          ease: 'power3.out',
          stagger: 0.1,
        });
      },
      start: 'top 88%',
      once: true,
    });
  }

  /* ── CTA Banner: dramatic scale-up reveal ── */
  const ctaBanner = document.querySelector('.cta-banner__inner');
  if (ctaBanner) {
    gsap.set(ctaBanner, { opacity: 0, scale: 0.92, y: 50 });
    ScrollTrigger.create({
      trigger: ctaBanner,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(ctaBanner, {
          opacity: 1, scale: 1, y: 0,
          duration: 1,
          ease: 'power3.out',
        });
      },
    });
  }

  /* ── Education Timeline: alternate slide from left/right ── */
  const eduEntries = document.querySelectorAll('.edu-timeline__entry');
  eduEntries.forEach((entry) => {
    const isLeft = entry.classList.contains('edu-timeline__entry--left');
    gsap.set(entry, { opacity: 0, x: isLeft ? -60 : 60 });
    ScrollTrigger.create({
      trigger: entry,
      start: 'top 88%',
      once: true,
      onEnter: () => {
        gsap.to(entry, {
          opacity: 1, x: 0,
          duration: 0.8,
          ease: 'power3.out',
        });
      },
    });
  });

  /* ── Education timeline line: grow from top ── */
  const eduLine = document.querySelector('.edu-timeline__line');
  if (eduLine) {
    gsap.set(eduLine, { scaleY: 0, transformOrigin: 'top center' });
    ScrollTrigger.create({
      trigger: eduLine,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(eduLine, {
          scaleY: 1,
          duration: 1.5,
          ease: 'power2.out',
        });
      },
    });
  }

  /* ── Vision Cards: stagger with subtle scale ── */
  const visionCards = document.querySelectorAll('.vision__card');
  if (visionCards.length) {
    gsap.set(visionCards, { opacity: 0, y: 50, scale: 0.95 });
    ScrollTrigger.batch(visionCards, {
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1, y: 0, scale: 1,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.12,
        });
      },
      start: 'top 85%',
      once: true,
    });
  }

  /* ── Contact: split halves slide in ── */
  const contactLeft = document.querySelector('.contact__left');
  const contactRight = document.querySelector('.contact__right');
  if (contactLeft) {
    gsap.set(contactLeft, { opacity: 0, x: -40 });
    ScrollTrigger.create({
      trigger: contactLeft,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(contactLeft, { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out' });
      },
    });
  }
  if (contactRight) {
    gsap.set(contactRight, { opacity: 0, x: 40 });
    ScrollTrigger.create({
      trigger: contactRight,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(contactRight, { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out', delay: 0.15 });
      },
    });
  }

  /* ── Marquee: fade in ── */
  const marquee = document.querySelector('.marquee');
  if (marquee) {
    gsap.set(marquee, { opacity: 0 });
    ScrollTrigger.create({
      trigger: marquee,
      start: 'top 92%',
      once: true,
      onEnter: () => {
        gsap.to(marquee, { opacity: 1, duration: 1, ease: 'power2.out' });
      },
    });
  }

  /* ── Section Labels: pop in ── */
  const sectionLabels = document.querySelectorAll('.career__label, .education__label, .vision__label, .contact__label, .competencies__label');
  sectionLabels.forEach((label) => {
    gsap.set(label, { opacity: 0, scale: 0.8, y: 10 });
    ScrollTrigger.create({
      trigger: label,
      start: 'top 90%',
      once: true,
      onEnter: () => {
        gsap.to(label, { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'back.out(1.7)' });
      },
    });
  });

  /* ── Parallax: decorative elements (desktop only) ── */
  if (isDesktop) {
    gsap.utils.toArray('.about__deco-circle, .about__deco-ring, .about__deco-grid').forEach((el) => {
      gsap.to(el, {
        y: -30,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });
  }

  /* ── Reveal on Scroll ── */
  const revealElements = document.querySelectorAll('.reveal');
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = entry.target.dataset.delay || 0;
          setTimeout(() => entry.target.classList.add('visible'), delay);
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -30px 0px' }
  );

  revealElements.forEach((el) => {
    if (el.closest('.about__bento')) return;
    const parent = el.closest('.about__cards, .competencies__grid, .vision__grid, .education__grid, .career__table');
    if (parent) {
      const siblings = parent.querySelectorAll('.reveal');
      const idx = Array.from(siblings).indexOf(el);
      el.dataset.delay = idx * 80;
    }
    revealObserver.observe(el);
  });

  /* ── Generic Animated Counter ── */
  function easeOutQuart(t) { return 1 - Math.pow(1 - t, 4); }

  function animateCounter(el) {
    if (el.dataset.animated) return;
    el.dataset.animated = '1';
    const target = parseInt(el.dataset.target, 10);
    const suffix = el.dataset.suffix || '';
    const duration = 2000;
    const start = performance.now();

    function tick(now) {
      const p = Math.min((now - start) / duration, 1);
      el.textContent = Math.round(target * easeOutQuart(p)) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  const counterEls = document.querySelectorAll('[data-target]');
  const counterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          animateCounter(e.target);
          counterObserver.unobserve(e.target);
        }
      });
    },
    { threshold: 0.5 }
  );
  counterEls.forEach((el) => counterObserver.observe(el));

  /* ── Sticky Navigation ── */
  const nav = document.getElementById('nav');
  const navLinkEls = document.querySelectorAll('.nav__links a');

  /* Handle external nav links disguised as anchors (avoids :visited) */
  navLinkEls.forEach(a => {
    const ext = a.dataset.href;
    if (ext) {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = ext;
      });
    }
  });

  /* Dynamic style injection to override :visited colors */
  const navColorStyle = document.createElement('style');
  document.head.appendChild(navColorStyle);

  function setNavLinkColor(c) {
    navColorStyle.textContent =
      '.nav__links a,.nav__links a:link,.nav__links a:visited,.nav__links a:active{color:' + c + '!important}' +
      '.nav__links a:hover{color:#1A1A1A!important}';
  }
  setNavLinkColor('#555');

  function handleScroll() {
    const scrolled = window.scrollY > 60;
    nav.classList.toggle('nav--scrolled', scrolled);
    setNavLinkColor(scrolled ? '#1A1A1A' : '#555');
  }
  window.addEventListener('scroll', handleScroll, { passive: true });

  /* ── Mobile Burger Menu ── */
  const burger = document.getElementById('navBurger');
  const navLinks = document.querySelector('.nav__links');

  if (burger && navLinks) {
    burger.addEventListener('click', () => {
      burger.classList.toggle('active');
      navLinks.classList.toggle('active');
    });
    navLinks.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        burger.classList.remove('active');
        navLinks.classList.remove('active');
      });
    });
  }

  /* ── Smooth Scroll ── */
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    if (anchor.classList.contains('nav__cta') || anchor.classList.contains('cal-trigger') || anchor.classList.contains('contact-trigger')) return;
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        const navH = nav ? nav.offsetHeight : 0;
        const pos = target.getBoundingClientRect().top + window.scrollY - navH - 20;
        window.scrollTo({ top: pos, behavior: 'smooth' });
      }
    });
  });

  /* ── Active nav highlight ── */
  const sections = document.querySelectorAll('section[id]');
  function highlightNav() {
    const y = window.scrollY + 120;
    sections.forEach((s) => {
      const link = document.querySelector('.nav__links a[href="#' + s.id + '"]');
      if (link) {
        link.style.color = (y >= s.offsetTop && y < s.offsetTop + s.offsetHeight)
          ? 'var(--color-text)' : '';
      }
    });
  }
  window.addEventListener('scroll', highlightNav, { passive: true });

  /* ── Stacking Sections (card stack scroll effect – desktop only) ── */
  const stackSections = gsap.utils.toArray('.stack-section');
  if (isDesktop) {
    stackSections.forEach((section, i) => {
      const isLast = i === stackSections.length - 1;
      const nextEl = isLast ? '.footer' : stackSections[i + 1];

      const isTall = () => section.offsetHeight > window.innerHeight;

      ScrollTrigger.create({
        trigger: section,
        start: () => isTall() ? 'bottom bottom' : 'top top',
        pin: true,
        pinSpacing: false,
        endTrigger: nextEl,
        end: 'top top',
        anticipatePin: 1,
      });
    });
  }

  /* ── Career Row Expand/Collapse ── */
  document.querySelectorAll('.career__row:not(.career__row--header)').forEach((row) => {
    row.addEventListener('click', () => {
      const wasActive = row.classList.contains('active');
      document.querySelectorAll('.career__row.active').forEach((r) => r.classList.remove('active'));
      if (!wasActive) row.classList.add('active');
    });
  });

  /* ── Footer reveal (desktop: fixed+animated, mobile: static) ── */
  const footer = document.querySelector('.footer');
  if (footer) {
    if (isDesktop) {
      gsap.set(footer, { y: 60, opacity: 0 });
      const lastSection = stackSections[stackSections.length - 1];
      if (lastSection) {
        ScrollTrigger.create({
          trigger: lastSection,
          start: 'top 20%',
          onEnter: () => gsap.to(footer, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }),
          onLeaveBack: () => gsap.to(footer, { y: 60, opacity: 0, duration: 0.4, ease: 'power3.in' }),
        });
      }
    } else {
      gsap.set(footer, { y: 0, opacity: 1 });
    }
  }

  /* ── Circuit Board Animation (CTA section) ── */
  const circuitCanvas = document.getElementById('circuitCanvas');
  if (circuitCanvas) {
    const ctx = circuitCanvas.getContext('2d');
    let cW, cH, edges, gridNodes, particles, centerX, centerY, maxDist;
    const glowColor = [140, 180, 255];
    const SPACING = 70;

    function resizeCircuit() {
      const rect = circuitCanvas.parentElement.getBoundingClientRect();
      cW = circuitCanvas.width = rect.width * window.devicePixelRatio;
      cH = circuitCanvas.height = rect.height * window.devicePixelRatio;
      circuitCanvas.style.width = rect.width + 'px';
      circuitCanvas.style.height = rect.height + 'px';
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      centerX = rect.width / 2;
      centerY = rect.height / 2;
      maxDist = Math.sqrt(centerX * centerX + centerY * centerY);
      buildCircuit(rect.width, rect.height);
    }

    function distAlpha(x, y) {
      const dx = x - centerX, dy = y - centerY;
      const d = Math.sqrt(dx * dx + dy * dy) / maxDist;
      return Math.max(0, 1 - d * 1.3);
    }

    // lines = array of { edges: [{x1,y1,x2,y2}, ...] } — each is a full path
    let lines = [];
    let pulseParticles = [];
    let lastPulse = 0;
    const PULSE_INTERVAL = 5000; // ms

    function buildCircuit(w, h) {
      edges = [];
      lines = [];
      pulseParticles = [];
      lastPulse = performance.now();

      const cols = Math.ceil(w / SPACING) + 1;
      const rows = Math.ceil(h / SPACING) + 1;
      const cCol = Math.round(centerX / SPACING);
      const cRow = Math.round(centerY / SPACING);
      const cx = cCol * SPACING, cy = cRow * SPACING;

      // Scale line count based on aspect ratio so tall viewports get more lines
      const aspect = Math.max(w, h) / Math.min(w, h);
      const numPairs = Math.round((5 + Math.floor(Math.random() * 3)) * Math.max(1, aspect * 0.7));

      // Scatter radius: lines start from positions around center
      const scatterR = Math.min(cols, rows) * 0.3;

      // Generate lines in pairs (original + mirror) for visual symmetry
      const dirPool = [[0,1],[1,0],[0,-1],[-1,0]];

      for (let i = 0; i < numPairs; i++) {
        // Start from a random position scattered around center
        const offsetR = Math.round((Math.random() - 0.5) * scatterR);
        const offsetC = Math.round((Math.random() - 0.5) * scatterR);
        let r = cRow + offsetR, c = cCol + offsetC;
        if (r < 0 || r >= rows || c < 0 || c >= cols) { r = cRow; c = cCol; }

        let dir = [...dirPool[Math.floor(Math.random() * dirPool.length)]];
        const lineEdges = [];
        const mirrorEdges = [];
        const steps = 5 + Math.floor(Math.random() * 8);

        for (let s = 0; s < steps; s++) {
          const nr = r + dir[0];
          const nc = c + dir[1];
          if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) break;

          const x1 = c * SPACING, y1 = r * SPACING;
          const x2 = nc * SPACING, y2 = nr * SPACING;

          if (distAlpha(x2, y2) < 0.05) break;

          const edge = { x1, y1, x2, y2 };
          lineEdges.push(edge);
          edges.push(edge);

          // Mirror across center
          const mx1 = 2 * cx - x1, my1 = 2 * cy - y1;
          const mx2 = 2 * cx - x2, my2 = 2 * cy - y2;
          if (mx2 >= 0 && mx2 <= w && my2 >= 0 && my2 <= h) {
            const mEdge = { x1: mx1, y1: my1, x2: mx2, y2: my2 };
            mirrorEdges.push(mEdge);
            edges.push(mEdge);
          }

          r = nr;
          c = nc;

          // 30% chance to turn perpendicular
          if (Math.random() > 0.7) {
            if (dir[0] === 0) {
              dir = Math.random() > 0.5 ? [1, 0] : [-1, 0];
            } else {
              dir = Math.random() > 0.5 ? [0, 1] : [0, -1];
            }
          }
        }

        if (lineEdges.length > 0) lines.push({ edges: lineEdges });
        if (mirrorEdges.length > 0) lines.push({ edges: mirrorEdges });
      }
    }

    function spawnPulse() {
      pulseParticles = [];
      lines.forEach((line) => {
        pulseParticles.push({
          line: line,
          edgeIdx: 0,
          t: 0,
          speed: 0.012 + Math.random() * 0.008,
          size: 2.5,
          glowSize: 16 + Math.random() * 8,
          alive: true,
        });
      });
    }

    function drawCircuit(timestamp) {
      const w = cW / window.devicePixelRatio;
      const h = cH / window.devicePixelRatio;
      ctx.clearRect(0, 0, w, h);

      // Pulse trigger every 5 seconds
      if (timestamp - lastPulse > PULSE_INTERVAL) {
        lastPulse = timestamp;
        spawnPulse();
      }

      // Draw static traces
      ctx.lineWidth = 1;
      edges.forEach((e) => {
        const mx = (e.x1 + e.x2) / 2, my = (e.y1 + e.y2) / 2;
        const alpha = distAlpha(mx, my) * 0.1;
        if (alpha < 0.003) return;
        ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
        ctx.beginPath();
        ctx.moveTo(e.x1, e.y1);
        ctx.lineTo(e.x2, e.y2);
        ctx.stroke();
      });

      // Animate pulse particles
      pulseParticles.forEach((p) => {
        if (!p.alive) return;

        p.t += p.speed;

        // Move to next edge segment when current one finishes
        if (p.t >= 1) {
          p.edgeIdx++;
          if (p.edgeIdx >= p.line.edges.length) {
            p.alive = false;
            return;
          }
          p.t = 0;
        }

        const e = p.line.edges[p.edgeIdx];
        const px = e.x1 + (e.x2 - e.x1) * p.t;
        const py = e.y1 + (e.y2 - e.y1) * p.t;

        // Fade out as it gets further from center
        const alpha = distAlpha(px, py);
        if (alpha < 0.02) { p.alive = false; return; }

        // Also fade near end of line
        const progress = (p.edgeIdx + p.t) / p.line.edges.length;
        const endFade = progress > 0.7 ? 1 - ((progress - 0.7) / 0.3) : 1;

        // Glow
        const ga = alpha * endFade;
        const grad = ctx.createRadialGradient(px, py, 0, px, py, p.glowSize);
        grad.addColorStop(0, `rgba(${glowColor[0]},${glowColor[1]},${glowColor[2]},${0.45 * ga})`);
        grad.addColorStop(0.4, `rgba(${glowColor[0]},${glowColor[1]},${glowColor[2]},${0.12 * ga})`);
        grad.addColorStop(1, `rgba(${glowColor[0]},${glowColor[1]},${glowColor[2]},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(px, py, p.glowSize, 0, Math.PI * 2);
        ctx.fill();

        // Core dot
        ctx.fillStyle = `rgba(${glowColor[0]},${glowColor[1]},${glowColor[2]},${0.9 * ga})`;
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      requestAnimationFrame(drawCircuit);
    }

    resizeCircuit();
    spawnPulse();
    requestAnimationFrame(drawCircuit);
    window.addEventListener('resize', resizeCircuit);
  }

  /* ── Mobile Hint Toast ── */
  const mobileHint = document.getElementById('mobileHint');
  const mobileHintClose = document.getElementById('mobileHintClose');
  if (mobileHint && mobileHintClose) {
    if (sessionStorage.getItem('mobileHintDismissed')) {
      mobileHint.style.display = 'none';
    }
    mobileHintClose.addEventListener('click', () => {
      mobileHint.classList.add('is-hidden');
      sessionStorage.setItem('mobileHintDismissed', '1');
      setTimeout(() => { mobileHint.style.display = 'none'; }, 400);
    });
  }

  /* ── Marquee: clone content for seamless loop ── */
  const marqueeContent = document.querySelector('.marquee__content');
  if (marqueeContent) {
    const clone = marqueeContent.cloneNode(true);
    marqueeContent.parentElement.appendChild(clone);
  }

  /* ── Contact Popup ── */
  const popupOverlay = document.getElementById('contactPopup');
  const popupCloseBtn = document.getElementById('contactPopupClose');
  const popupForm = document.getElementById('contactPopupForm');
  const popupFormWrap = document.getElementById('contactPopupFormWrap');
  const popupSuccess = document.getElementById('contactPopupSuccess');

  var scrollLockY = 0;

  function lockScroll() {
    scrollLockY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollLockY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    if (lenis) lenis.stop();
  }
  function unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    window.scrollTo(0, scrollLockY);
    if (lenis) lenis.start();
  }

  function openContactPopup(e) {
    if (!popupOverlay) return;
    e.preventDefault();
    e.stopPropagation();
    popupOverlay.classList.add('active');
    lockScroll();
  }
  function closeContactPopup() {
    if (!popupOverlay) return;
    popupOverlay.classList.remove('active');
    unlockScroll();
  }

  // Attach to all .nav__cta links
  document.querySelectorAll('.nav__cta').forEach(function (btn) {
    btn.addEventListener('click', openContactPopup);
  });

  if (popupCloseBtn) popupCloseBtn.addEventListener('click', closeContactPopup);
  if (popupOverlay) {
    popupOverlay.addEventListener('click', function (e) {
      if (e.target === popupOverlay) closeContactPopup();
    });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && popupOverlay && popupOverlay.classList.contains('active')) closeContactPopup();
  });

  // EmailJS config – replace these with your actual IDs from https://www.emailjs.com
  var EMAILJS_PUBLIC_KEY = 'xDBXw5SkVCdcD9ZPK';   // Dashboard → Account → Public Key
  var EMAILJS_SERVICE_ID = 'service_2bl0uzg';   // Dashboard → Email Services → Service ID
  var EMAILJS_TEMPLATE_ID = 'template_fb3b2op'; // Dashboard → Email Templates → Template ID

  emailjs.init(EMAILJS_PUBLIC_KEY);

  // Form submit → EmailJS
  if (popupForm) {
    var submitBtn = popupForm.querySelector('.contact-popup__submit');
    popupForm.addEventListener('submit', function (e) {
      e.preventDefault();

      // Basic validation
      if (!popupForm.checkValidity()) {
        popupForm.reportValidity();
        return;
      }

      var name = popupForm.querySelector('[name="name"]').value.trim();
      var email = popupForm.querySelector('[name="email"]').value.trim();
      var subject = popupForm.querySelector('[name="subject"]').value.trim();
      var message = popupForm.querySelector('[name="message"]').value.trim();

      // Disable button while sending
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Wird gesendet…';
      }

      emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        from_name: name,
        from_email: email,
        subject: subject || 'Kontaktanfrage über Website',
        message: message
      }).then(function () {
        // Show success
        if (popupFormWrap && popupSuccess) {
          popupFormWrap.style.display = 'none';
          popupSuccess.classList.add('show');
          setTimeout(function () {
            closeContactPopup();
            popupFormWrap.style.display = '';
            popupSuccess.classList.remove('show');
            popupForm.reset();
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = 'Nachricht senden →';
            }
          }, 3500);
        }
      }).catch(function (err) {
        console.error('EmailJS error:', err);
        alert('Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder schreiben Sie direkt an leonstoeckl@icloud.com');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Nachricht senden →';
        }
      });
    });
  }

  /* ── Calendar Popup ── */
  var calOverlay = document.getElementById('calPopup');
  var calCloseBtn = document.getElementById('calPopupClose');
  var calDaysEl = document.getElementById('calDays');
  var calMonthYearEl = document.getElementById('calMonthYear');
  var calPrevBtn = document.getElementById('calPrev');
  var calNextBtn = document.getElementById('calNext');
  var calSlotsEl = document.getElementById('calSlots');
  var calSelectedDateEl = document.getElementById('calSelectedDate');
  var calConfirmEl = document.getElementById('calConfirm');
  var calConfirmBtn = document.getElementById('calConfirmBtn');
  var calLeftEl = document.getElementById('calLeft');
  var calSuccessEl = document.getElementById('calSuccess');
  var calNameInput = document.getElementById('cal-name');
  var calEmailInput = document.getElementById('cal-email');
  var calChosenEl = document.getElementById('calChosen');
  var calChosenText = document.getElementById('calChosenText');
  var calChosenEdit = document.getElementById('calChosenEdit');

  var calState = {
    viewYear: 0,
    viewMonth: 0,
    selectedDate: null,
    selectedSlot: null
  };

  var MONTH_NAMES_DE = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  var WEEKDAY_NAMES_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  var TIME_SLOTS = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'];

  function formatDateDE(y, m, d) {
    var dow = WEEKDAY_NAMES_SHORT[new Date(y, m, d).getDay()];
    return dow + ', ' + d + '. ' + MONTH_NAMES_DE[m] + ' ' + y;
  }

  function formatDateShort(y, m, d) {
    var dow = WEEKDAY_NAMES_SHORT[new Date(y, m, d).getDay()];
    return dow + ', ' + d + '. ' + MONTH_NAMES_DE[m].substring(0, 3) + '.';
  }

  function renderCalendar() {
    if (!calDaysEl) return;
    var y = calState.viewYear;
    var m = calState.viewMonth;
    var today = new Date();
    today.setHours(0, 0, 0, 0);

    calMonthYearEl.textContent = MONTH_NAMES_DE[m] + ' ' + y;

    var nowY = today.getFullYear();
    var nowM = today.getMonth();
    if (calPrevBtn) calPrevBtn.disabled = (y === nowY && m === nowM);

    var firstDay = new Date(y, m, 1).getDay();
    var offset = (firstDay === 0 ? 6 : firstDay - 1);
    var daysInMonth = new Date(y, m + 1, 0).getDate();

    var html = '';
    for (var i = 0; i < offset; i++) {
      html += '<span class="cal-popup__day empty"></span>';
    }
    for (var d = 1; d <= daysInMonth; d++) {
      var dt = new Date(y, m, d);
      dt.setHours(0, 0, 0, 0);
      var isPast = dt < today;
      var isWknd = dt.getDay() === 0 || dt.getDay() === 6;
      var isToday = dt.getTime() === today.getTime();
      var isAvail = !isPast && !isWknd;
      var isSel = calState.selectedDate &&
        calState.selectedDate.getTime() === dt.getTime();

      var cls = 'cal-popup__day';
      if (isPast) cls += ' past';
      if (isWknd) cls += ' weekend';
      if (isAvail) cls += ' available';
      if (isToday) cls += ' today';
      if (isSel) cls += ' selected';

      if (isAvail) {
        html += '<button class="' + cls + '" data-day="' + d + '">' + d + '</button>';
      } else {
        html += '<span class="' + cls + '">' + d + '</span>';
      }
    }
    calDaysEl.innerHTML = html;
  }

  function showSlots() {
    if (!calSlotsEl || !calState.selectedDate) return;
    var html = '';
    TIME_SLOTS.forEach(function (slot) {
      var cls = 'cal-popup__slot';
      if (calState.selectedSlot === slot) cls += ' active';
      html += '<button class="' + cls + '" data-slot="' + slot + '">' + slot + ' Uhr</button>';
    });
    calSlotsEl.innerHTML = html;

    var dateStr = formatDateDE(
      calState.selectedDate.getFullYear(),
      calState.selectedDate.getMonth(),
      calState.selectedDate.getDate()
    );
    if (calSelectedDateEl) calSelectedDateEl.textContent = dateStr;
  }

  function showConfirmStep() {
    if (!calState.selectedDate || !calState.selectedSlot) return;
    var shortDate = formatDateShort(
      calState.selectedDate.getFullYear(),
      calState.selectedDate.getMonth(),
      calState.selectedDate.getDate()
    );
    // Update chosen chip
    if (calChosenText) calChosenText.textContent = shortDate + ' · ' + calState.selectedSlot + ' Uhr';
    if (calChosenEl) calChosenEl.classList.add('show');
    if (calSelectedDateEl) calSelectedDateEl.style.display = 'none';
    // Slide: slots out, confirm in
    if (calSlotsEl) calSlotsEl.classList.add('slide-out');
    if (calConfirmEl) calConfirmEl.classList.add('show');
  }

  function showSlotsStep() {
    // Slide: confirm out, slots in
    if (calConfirmEl) calConfirmEl.classList.remove('show');
    if (calSlotsEl) calSlotsEl.classList.remove('slide-out');
    if (calChosenEl) calChosenEl.classList.remove('show');
    if (calSelectedDateEl) calSelectedDateEl.style.display = '';
    calState.selectedSlot = null;
    showSlots();
  }

  function resetCalPopup() {
    calState.selectedDate = null;
    calState.selectedSlot = null;
    if (calSlotsEl) {
      calSlotsEl.classList.remove('slide-out');
      calSlotsEl.innerHTML = '<p class="cal-popup__slots-empty">Bitte wählen Sie zuerst einen Tag im Kalender aus.</p>';
    }
    if (calSelectedDateEl) {
      calSelectedDateEl.textContent = 'Wählen Sie ein Datum →';
      calSelectedDateEl.style.display = '';
    }
    if (calConfirmEl) calConfirmEl.classList.remove('show');
    if (calConfirmBtn) {
      calConfirmBtn.disabled = true;
      calConfirmBtn.textContent = 'Termin anfragen →';
    }
    if (calNameInput) calNameInput.value = '';
    if (calEmailInput) calEmailInput.value = '';
    if (calChosenEl) calChosenEl.classList.remove('show');
    if (calSuccessEl) calSuccessEl.classList.remove('show');
    if (calLeftEl) calLeftEl.style.display = '';
    var rightEl = calOverlay && calOverlay.querySelector('.cal-popup__right');
    if (rightEl) rightEl.style.display = '';
  }

  function openCalPopup(e) {
    if (!calOverlay) return;
    e.preventDefault();
    e.stopPropagation();
    var now = new Date();
    calState.viewYear = now.getFullYear();
    calState.viewMonth = now.getMonth();
    resetCalPopup();
    renderCalendar();
    calOverlay.classList.add('active');
    lockScroll();
  }

  function closeCalPopup() {
    if (!calOverlay) return;
    calOverlay.classList.remove('active');
    unlockScroll();
  }

  // Triggers
  document.querySelectorAll('.cal-trigger').forEach(function (btn) {
    btn.addEventListener('click', openCalPopup);
  });
  document.querySelectorAll('.contact-trigger').forEach(function (btn) {
    btn.addEventListener('click', openContactPopup);
  });

  if (calCloseBtn) calCloseBtn.addEventListener('click', closeCalPopup);
  if (calOverlay) {
    calOverlay.addEventListener('click', function (e) {
      if (e.target === calOverlay) closeCalPopup();
    });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && calOverlay && calOverlay.classList.contains('active')) closeCalPopup();
  });

  // Month navigation
  if (calPrevBtn) calPrevBtn.addEventListener('click', function () {
    calState.viewMonth--;
    if (calState.viewMonth < 0) { calState.viewMonth = 11; calState.viewYear--; }
    renderCalendar();
  });
  if (calNextBtn) calNextBtn.addEventListener('click', function () {
    calState.viewMonth++;
    if (calState.viewMonth > 11) { calState.viewMonth = 0; calState.viewYear++; }
    renderCalendar();
  });

  // Day click
  if (calDaysEl) calDaysEl.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-day]');
    if (!btn) return;
    var d = parseInt(btn.getAttribute('data-day'), 10);
    calState.selectedDate = new Date(calState.viewYear, calState.viewMonth, d);
    calState.selectedSlot = null;
    // Reset to slots view
    if (calConfirmEl) calConfirmEl.classList.remove('show');
    if (calSlotsEl) calSlotsEl.classList.remove('slide-out');
    if (calChosenEl) calChosenEl.classList.remove('show');
    if (calSelectedDateEl) calSelectedDateEl.style.display = '';
    renderCalendar();
    showSlots();
  });

  // Slot click → slide to confirm
  if (calSlotsEl) calSlotsEl.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-slot]');
    if (!btn) return;
    calState.selectedSlot = btn.getAttribute('data-slot');
    showSlots();
    // Small delay for the active state to register visually
    setTimeout(function () {
      showConfirmStep();
    }, 180);
  });

  // Edit button on chosen chip → back to slots
  if (calChosenEdit) calChosenEdit.addEventListener('click', showSlotsStep);

  // Enable confirm button based on inputs
  function checkCalConfirm() {
    if (!calConfirmBtn || !calNameInput || !calEmailInput) return;
    calConfirmBtn.disabled = !(
      calNameInput.value.trim() &&
      calEmailInput.value.trim() &&
      calState.selectedSlot
    );
  }
  if (calNameInput) calNameInput.addEventListener('input', checkCalConfirm);
  if (calEmailInput) calEmailInput.addEventListener('input', checkCalConfirm);

  // Submit booking via EmailJS
  if (calConfirmBtn) {
    calConfirmBtn.addEventListener('click', function () {
      if (!calState.selectedDate || !calState.selectedSlot) return;
      var name = calNameInput.value.trim();
      var email = calEmailInput.value.trim();
      if (!name || !email) return;

      var dateStr = formatDateDE(
        calState.selectedDate.getFullYear(),
        calState.selectedDate.getMonth(),
        calState.selectedDate.getDate()
      );

      calConfirmBtn.disabled = true;
      calConfirmBtn.textContent = 'Wird gesendet…';

      emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        from_name: name,
        from_email: email,
        subject: 'Terminanfrage: Erstgespräch am ' + dateStr + ' um ' + calState.selectedSlot + ' Uhr',
        message: 'Terminanfrage für ein Erstgespräch:\n\nDatum: ' + dateStr + '\nUhrzeit: ' + calState.selectedSlot + ' Uhr\nName: ' + name + '\nE-Mail: ' + email
      }).then(function () {
        if (calLeftEl) calLeftEl.style.display = 'none';
        var rightEl = calOverlay.querySelector('.cal-popup__right');
        if (rightEl) rightEl.style.display = 'none';
        if (calSuccessEl) calSuccessEl.classList.add('show');
        setTimeout(function () {
          closeCalPopup();
          resetCalPopup();
        }, 3500);
      }).catch(function (err) {
        console.error('EmailJS calendar error:', err);
        alert('Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.');
        calConfirmBtn.disabled = false;
        calConfirmBtn.textContent = 'Termin anfragen →';
      });
    });
  }

})();
